import * as React from 'react';

import { translate } from '~l10n/client';

import { UIButton } from '~shared/components/buttons';
import { Container } from '~shared/components/Container';
import { Dialog } from '~shared/components/Dialog';
import { Divider } from '~shared/components/Divider';
import { Inline } from '~shared/components/Inline';
import { toast } from '~shared/components/Toast';
import {
  CopyToWorkspaceProvider,
  useCopyDialog,
} from '~teacher/components/quizzes/CopyMoveDialog/CopyToWorkspaceDialog/CopyToWorkspaceContext';
import { SelectFolder } from '~teacher/components/quizzes/CopyMoveDialog/CopyToWorkspaceDialog/SelectFolder';
import { SelectWorkspace } from '~teacher/components/quizzes/CopyMoveDialog/CopyToWorkspaceDialog/SelectWorkspace';
import { getCopyToWorkspaceDialogTitle } from '~teacher/components/quizzes/CopyMoveDialog/CopyToWorkspaceDialog/utils';
import { useWorkspaceQuizzes } from '~teacher/hook/workspaces';
import { useCopyWorkspaceQuizToWorkspace } from '~teacher/hook/workspaces/useCopyWorkspaceQuizToWorkspace';

interface Props {
  onClose: () => void;
  isOpen: boolean;
  itemIdsToMove: string[];
  sourceWorkspace: string;
}
export const CopyQuizToWorkspaceDialog = ({
  isOpen,
  onClose,
  itemIdsToMove,
  sourceWorkspace,
}: Props) => {
  return isOpen ? (
    <CopyToWorkspaceProvider>
      <DialogComponent
        itemIdsToMove={itemIdsToMove}
        isOpen={isOpen}
        onClose={onClose}
        sourceWorkspace={sourceWorkspace}
      />
    </CopyToWorkspaceProvider>
  ) : null;
};

const DialogComponent = ({
  isOpen,
  onClose,
  itemIdsToMove,
  sourceWorkspace,
}: Props) => {
  const { workspaceId, parentId, currentStep } = useCopyDialog();
  const [loading, setLoading] = React.useState(false);
  const copyQuizToWorkspaceMutation = useCopyWorkspaceQuizToWorkspace();
  const handleCopy = React.useCallback(() => {
    setLoading(true);

    copyQuizToWorkspaceMutation.mutate(
      {
        workspaceId,
        from: sourceWorkspace,
        parentId,
        quizIds: itemIdsToMove,
      },
      {
        onSuccess: () => {
          setLoading(false);
          toast(translate('Creating copies'));
          onClose();
        },
        onError: () => toast.critical(translate('An error has occurred')),
      }
    );
  }, [
    copyQuizToWorkspaceMutation,
    itemIdsToMove,
    onClose,
    parentId,
    sourceWorkspace,
    workspaceId,
  ]);

  const quizzesToCopy = useWorkspaceQuizzes({
    workspaceId: sourceWorkspace,
    select: (workspaceQuizzes) =>
      workspaceQuizzes.filter((value) => itemIdsToMove.includes(value.id)),
  });

  const title = getCopyToWorkspaceDialogTitle(quizzesToCopy?.data ?? []);

  return (
    <Dialog
      closeLabel={translate('Close')}
      open={isOpen}
      onClose={onClose}
      id={'copy-to-workspace-dialog'}
      title={title}
    >
      {/* dialog doesnt unmount, so move to separate component to unmount properly */}
      {isOpen ? (
        <div className={'w-full h-modal-lg flex flex-col'}>
          {/* top divider */}
          <div className={'pt-4'}>
            <Divider />
          </div>
          {/* steps are rendered inside here */}
          <Container className={'overflow-hidden'} width={1}>
            <div className={'flex-grow overflow-y-scroll pt-4'}>
              {currentStep === 0 ? <SelectWorkspace /> : null}
              {currentStep === 1 ? <SelectFolder /> : null}
            </div>
          </Container>
          {/* bottom buttons */}
          <Container
            className={'justify-end'}
            row
            margin={'20px 0 0 '}
            verticalCenter
          >
            <Inline align="right">
              <UIButton isDisabled={loading} onClick={onClose}>
                {translate('Cancel')}
              </UIButton>
              <UIButton
                isLoading={loading}
                onClick={handleCopy}
                isDisabled={!workspaceId}
                appearance="primary"
                a11yLabel="copy-folder-button"
              >
                {translate('Copy')}
              </UIButton>
            </Inline>
          </Container>
        </div>
      ) : null}
    </Dialog>
  );
};
